// src/encounter/lib/to-vitals-block-items.ts
// PROD-2577 (part B) — VitalsRowVitals → VitalsBlockItem[], the mapping step behind the
// QuestionnaireRenderer `resolveVitals` host resolver. Deliberately does NOT re-derive the
// observations→vitals mapping: `patientVitalsToRow` (to-vitals-row.ts) already reduces the
// backend PatientVital[] to a VitalsRowVitals, and this file is the ONLY thing downstream of
// it that changes — the shape the new DS `VitalsBlock` (layout="pills") wants instead of the
// shape `EncounterVitalsStrip` wanted.
//
// TILE CATALOGUE + patientType gating mirror the DS's own EncounterVitalsStrip
// (CORE_TILES/EXTENDED_TILES/context tiles, `@scripsteam/scrips-signal-ds`
// organisms/EncounterVitalsStrip/EncounterVitalsStrip.tsx) — reproduced here because those
// consts are module-private (only `preEncounterVitalSpecs`, `badgeOf` and
// `DELTA_DISPLAY_MAX_PCT` are exported), not because the mapping differs.
//
// TONE/BADGE — honest feeds only (D5, Samer 2026-07-22): a tile's tone and badge text come
// only from the backend-classified `interpretation` / `semantic` already on the VitalReading
// (via `badgeOf`, exported), never a client-recomputed threshold.
import {
  badgeOf,
  type VitalReading,
  type VitalsBlockItem,
  type VitalsRowPatientType,
  type VitalsRowVitals,
} from '@scripsteam/scrips-signal-ds';
import { encounterVitalsFor } from './encounter-vitals';

// `VitalsBlockTone` itself is not re-exported from the package barrel (only `VitalsBlock`,
// `VitalsBlockProps` and `VitalsBlockItem` are) — derived from the item's own field instead of
// re-declaring the union by hand, so it can never drift from the real prop type.
type VitalsBlockTone = NonNullable<VitalsBlockItem['tone']>;

// The tile list (order, names, presence, patient-type gating) is the encounter's ONE vitals list (PROD-3130,
// encounter-vitals.ts), shared with the add-vitals form. History of the rules it carries: the PROD-1533 extended set
// is ALWAYS drawn, a dash pill when unread (production's PE row, dev-provider, measured 2026-09-25 on a 16y female
// with no readings; trust class P2, live app, no PE anchor); PROD-1274 context vitals are gated on patientType
// exactly as EncounterVitalsStrip gates them; head circumference has no `VitalsBlock` icon-label match (production's
// PE row never draws it), so it is drawn only when recorded.

/** Mirrors EncounterVitalsStrip's (non-exported) `statusOf`: backend-classified only. */
function toneOf(reading: VitalReading | undefined): VitalsBlockTone {
  switch (reading?.interpretation) {
    case 'critical-high':
    case 'critical-low':
      return 'bad';
    case 'high':
    case 'low':
      return 'warn';
    case 'normal':
      return 'good';
    default:
      break;
  }
  if (reading?.semantic === 'error') return 'bad';
  if (reading?.semantic === 'warning') return 'warn';
  return 'neutral';
}

/** Mirrors EncounterVitalsStrip's (non-exported) `flatStatusBadge`: the out-of-range word
 *  wins, else the exported `badgeOf`'s NI / %Δ. */
function badgeTextOf(reading: VitalReading | undefined): string | undefined {
  if (!reading) return undefined;
  switch (reading.interpretation) {
    case 'critical-high':
      return 'Critically high';
    case 'critical-low':
      return 'Critically low';
    case 'high':
      return 'High';
    case 'low':
      return 'Low';
    default:
      break;
  }
  const badge = badgeOf(reading);
  if (!badge) return undefined;
  if (badge.kind === 'ni') return 'NI';
  if (badge.kind === 'delta' && badge.value) return badge.value;
  return undefined;
}

/** PROD-2962 (OL-1308 F2, design desk): vitals whose % change means nothing clinically — never shown. */
const NO_PERCENT_CHANGE: ReadonlySet<keyof VitalsRowVitals> = new Set<keyof VitalsRowVitals>(['temp', 'o2Sat']);

/** The word frame v2 shows for a reading the backend marks abnormal with no out-of-range word. */
export const ABNORMAL_WORD = 'Abnormal';

/** Is this badge a % change (not the backend's out-of-range word, not NI)? */
function isPercentChange(reading: VitalReading | undefined, badge: string | undefined): boolean {
  if (!reading || !badge) return false;
  const b = badgeOf(reading);
  return b?.kind === 'delta' && b.value === badge;
}

/** The backend marked the reading abnormal (`vitalNormalStatus`, carried as the DS-020 semantic) but
 *  gave no out-of-range word (High / Low / Critically …). Backend-classified only, never recomputed. */
function abnormalWithoutWord(reading: VitalReading | undefined): boolean {
  if (!reading) return false;
  const worded = reading.interpretation && reading.interpretation !== 'normal';
  return !worded && (reading.semantic === 'warning' || reading.semantic === 'error');
}

export interface VitalsBlockOptions {
  /**
   * PROD-2962 (OL-1308 F2, design desk D-67, frame v2 only). A % change badge says "change", so its
   * colour must never be the only thing saying "abnormal" (D-38):
   *   · a reading the backend marks abnormal with no out-of-range word shows the WORD "Abnormal" in the
   *     badge, in place of any % change, in the SAME warning tone as High / Low (D-67 amended: the
   *     backend flag does not mean worse than High);
   *   · every other % change is neutral, and temperature and SpO2 show none;
   *   · out-of-range words (High, Low, Critically …) keep their word and colour.
   * Interim until PROD-2995 (signal-ds: VitalsBlockItem.abnormal → VitalPill colours the value); then
   * the % returns, neutral. Absent (OFF) → exactly as before.
   */
  frameV2?: boolean;
}

/** The frame v2 badge and tone for one reading (D-67). */
function frameV2Badge(key: keyof VitalsRowVitals, reading: VitalReading | undefined): { badge?: string; tone: VitalsBlockTone } {
  if (abnormalWithoutWord(reading)) return { badge: ABNORMAL_WORD, tone: 'warn' };
  const badge = badgeTextOf(reading);
  if (!isPercentChange(reading, badge)) return { badge, tone: badge ? toneOf(reading) : 'neutral' };
  if (NO_PERCENT_CHANGE.has(key)) return { tone: 'neutral' };
  return { badge, tone: 'neutral' };
}

/**
 * `VitalsRowVitals` (the SAME reduction `patientVitalsToRow` already produces for
 * `peVitals`/the pre-encounter rail) → `VitalsBlockItem[]` for the DS `resolveVitals` host
 * resolver. No observation is re-fetched or re-derived here.
 */
export function vitalsRowToBlockItems(
  vitals: VitalsRowVitals,
  patientType?: VitalsRowPatientType,
  options: VitalsBlockOptions = {},
): VitalsBlockItem[] {
  const tiles = encounterVitalsFor(patientType);
  const items: VitalsBlockItem[] = [];
  for (const spec of tiles) {
    const reading = vitals[spec.key];
    if (spec.presence === 'reading' && !reading) continue;
    const plain = badgeTextOf(reading);
    const { badge, tone } = options.frameV2
      ? frameV2Badge(spec.key, reading)
      : // Tone is meaningful only alongside a badge — an unbadged reading stays 'neutral' so no
        // colour is implied without a visible word carrying it (StatusChip / VitalPill both
        // require the badge text to carry the meaning, never colour alone).
        { badge: plain, tone: plain ? toneOf(reading) : ('neutral' as VitalsBlockTone) };
    items.push({
      id: spec.key,
      label: spec.label,
      value: reading?.value !== undefined ? String(reading.value) : '',
      unit: reading?.unit,
      badge,
      tone,
    });
  }
  return items;
}
